// ./src/components/features/CreateEventWizard/steps/TicketsStep.tsx
//
// "What are you selling?" — the wizard's Tickets step (ROADMAP A2b #62 P6b). Shown only for a TICKETED event, right
// after the essentials, so the event already exists and everything here is REAL: a ticket type or discount code added
// here is a row on the event the moment its drawer saves, exactly as on the event's own Tickets page.
//
// What a host selling tickets needs before they can take money, in the order they decide it:
//   1. HOW THEY GET PAID — the currency (it LOCKS once money is recorded, so it is decided before any price), who pays
//      the fees, the tax rate, and whether card payments can run at all (Stripe connected). Each saves on change.
//   2. THE TICKET TYPES — through the event's own `TicketDrawer`, never a reduced form: a second editor for the same
//      thing drifts, and the wizard would create tickets the Tickets page then shows differently.
//      Each ticket type also opens its PRICE RULES (date / weekday / quantity discounts — which is why there is no
//      "Early bird" starter: that is a rule on a ticket, not a ticket) and, when Vouchers are on, how many VOUCHERS
//      each ticket gives — the event's own `TicketRulesDrawer` / `TicketCouponGrantsDrawer`.
//   3. DISCOUNT CODES (optional) — through the event's own `DiscountCodeDrawer`.
//   ⚠ VOUCHER TYPES ARE MADE ON THE STEP BEFORE THIS ONE (owner, 2026-09-29): a grant rule names a voucher type, so
//   the types must exist first. This step only says how many each ticket gives.
//
// ⚠ THE CARD-PAYMENT LINE IS A FACT OR NOTHING — `CardPaymentsNotice` (shared with the quick ticket sale, #148).
// ⚠ Drawers here are opened from state, not a URL stage: the wizard is one route whose step lives in the persisted
//   draft (as `VenuePickerField`'s in-place venue drawer does). A reload restores this step; the drawer's work is
//   either saved already or was never sent.

import React, { useCallback, useMemo, useState } from 'react';
import { Stack, Group, Text, Paper, Button, ActionIcon, Badge, Skeleton, Alert, Box, Grid } from '@mantine/core';
import { OpResult } from '@sdflc/api-helpers';
import { useTranslation } from 'react-i18next';

import { FEATURE_CODES } from '@/config';
import { useCurrentEvent, useOptionalEventSetupCatalogContext } from '@/contexts';
import { useCreateGate, useDiscountCodes, useEvent, useFeatureGate, useTicketTypes, useToast } from '@/hooks';
import { DiscountCode, Event, EventInput, EventService, TicketType, TicketTypeService, DiscountCodeService } from '@/utils/api';
import { formatCurrency, formatErrors, getFormErrors, refusalReason } from '@/utils';
import { currencySelectOptions, feeModeOf, feeModeOptions, serviceFeePayerFor } from '@/utils/eventSelling';
import {
  AppIcon,
  ConfirmModal,
  ErrorAlert,
  LoadMoreButton,
  PlanFeatureNotice,
  SelectField,
  TaxSelect,
} from '@/components/generic';
import { TicketDrawer } from '@/pages/private/EventTicketsPage/TicketDrawer';
import { TicketRulesDrawer } from '@/pages/private/EventTicketsPage/TicketRulesDrawer';
import { TicketCouponGrantsDrawer } from '@/pages/private/EventTicketsPage/TicketCouponGrantsDrawer';
import { DiscountCodeDrawer } from '@/pages/private/EventDiscountCodesPage/DiscountCodeDrawer';

import { useEventWizard } from '../WizardContext';
import { CardPaymentsNotice, useCardPayments } from '@/components/features/CardPayments';
import { SCHEDULE_MODE } from '../wizardTypes';

// Server names the selling card renders under its own controls; anything else lands in the card's alert.
const SELLING_FIELDS = ['currencyCode', 'feeMode', 'taxSettingId'];

/**
 * A new host's usual first tickets — each opens the real drawer with the name filled in.
 * ⚠ No "Early bird": an early-bird price is a PRICE RULE on a ticket (by date, weekday, quantity), not a ticket of its
 * own — offering it here would teach the workaround instead of the feature (owner, 2026-09-29).
 */
const TICKET_SUGGESTIONS = [
  { key: 'general', label: 'General admission' },
  { key: 'vip', label: 'VIP' },
];

interface TicketDrawerState {
  ticket: TicketType | null;
  defaultName?: string;
}

interface PendingRemove {
  kind: 'ticket' | 'code';
  id: string;
  name: string;
}

export const TicketsStep: React.FC = () => {
  const { t } = useTranslation(['events', 'common']);
  const toast = useToast();
  const { draft, createdEventId: eventId, saving: wizardSaving } = useEventWizard();
  const catalog = useOptionalEventSetupCatalogContext();

  // ── The event's selling settings ────────────────────────────────────────────────────────────────────────────────
  const { event, error: eventError, setEvent, reload: reloadEvent } = useEvent(eventId);
  // The wizard's own current event (CreateEventWizard) — what the later steps' drawers price amounts in.
  const { reload: reloadCurrentEvent } = useCurrentEvent();
  const [sellingSaving, setSellingSaving] = useState(false);
  const [sellingError, setSellingError] = useState<OpResult | null>(null);
  const { fields: sellingFieldErrors, genericErrors: sellingGeneric } = useMemo(
    () => getFormErrors(sellingError, SELLING_FIELDS),
    [sellingError],
  );
  const currencyData = useMemo(() => currencySelectOptions(), []);
  const feeModeData = useMemo(() => feeModeOptions(t), [t]);
  const currency = event?.currencyCode ?? null;

  /** One selling field, saved at once. The control shows the SERVER's value, so a refused change never looks applied. */
  const saveSelling = useCallback(
    async (patch: EventInput) => {
      if (!eventId) {
        return;
      }

      setSellingSaving(true);
      setSellingError(null);

      const res = await EventService.update({ id: eventId }, patch);

      setSellingSaving(false);

      if (!res.didSucceed()) {
        setSellingError(res);

        return;
      }

      setEvent((current) => (current ? ({ ...current, ...patch } as Event) : current));

      // ⚠ A new currency must reach the drawers of the steps after this one (a sponsor package, an artist's fee).
      if ('currencyCode' in patch) {
        reloadCurrentEvent();
      }
    },
    [eventId, setEvent, reloadCurrentEvent],
  );

  // ── Can this event take card payments? ──────────────────────────────────────────────────────────────────────────
  const cardPayments = useCardPayments(true);

  // ── Ticket types + discount codes (real rows on the event) ──────────────────────────────────────────────────────
  const {
    tickets,
    isLoaded: ticketsLoaded,
    loading: ticketsLoading,
    loadingMore,
    hasMore,
    error: ticketsError,
    reload: reloadTickets,
    loadMore,
  } = useTicketTypes(eventId);
  const { codes, isLoaded: codesLoaded, error: codesError, reload: reloadCodes } = useDiscountCodes(eventId);

  // The plan refuses the NEXT create, never what is already there — the Tickets page's gate. ⓘ Discount codes have none:
  // every plan since 2026-10-22 (owner).
  const ticketGate = useCreateGate(FEATURE_CODES.TICKETING, { eventId });

  const [ticketDrawer, setTicketDrawer] = useState<TicketDrawerState | null>(null);
  const [codeDrawer, setCodeDrawer] = useState<{ code: DiscountCode | null } | null>(null);
  // A ticket type's price rules / voucher grants — the Tickets page's own drawers.
  const [rulesFor, setRulesFor] = useState<TicketType | null>(null);
  const [grantsFor, setGrantsFor] = useState<TicketType | null>(null);
  // Both drawers still open on a plan without `promotions` — they explain and offer Subscribe (the page's model).
  const { checkFeature } = useFeatureGate();
  const promotionsLocked = !checkFeature(FEATURE_CODES.PROMOTIONS, { eventId }).allowed;
  const [pendingRemove, setPendingRemove] = useState<PendingRemove | null>(null);
  const [removing, setRemoving] = useState(false);

  const openTicket = (ticket: TicketType | null, defaultName?: string) => {
    if (!ticket && !ticketGate.canCreate) {
      return;
    }

    setTicketDrawer({ ticket, defaultName });
  };

  const openCode = (code: DiscountCode | null) => {
    setCodeDrawer({ code });
  };

  const confirmRemove = async () => {
    if (!pendingRemove) {
      return;
    }

    setRemoving(true);

    const res =
      pendingRemove.kind === 'ticket'
        ? await TicketTypeService.remove(pendingRemove.id)
        : await DiscountCodeService.remove(pendingRemove.id);

    setRemoving(false);

    if (!res.didSucceed()) {
      toast.error(refusalReason(res));

      return;
    }

    setPendingRemove(null);

    if (pendingRemove.kind === 'ticket') {
      await reloadTickets();
    } else {
      await reloadCodes();
    }
  };

  const hasPaidTicket = tickets.some((tk) => (tk.price ?? 0) > 0 || !!tk.isPayWhatYouWant);
  const recurring = draft.scheduleMode === SCHEDULE_MODE.RECURRING;

  // The type's own voucher idea ("Free first drink" for networking), when it has one.
  const type = catalog?.findType(draft.eventCategoryId) ?? null;
  const voucherIdea = type?.offers?.find((o) => o.featureCode === 'vouchers')?.options?.voucherIdeas?.[0] ?? null;
  const vouchersOn = (draft.featuresOn ?? []).includes('vouchers');

  const priceLabel = (tk: TicketType): string => {
    if (tk.isPayWhatYouWant) {
      return t('events:tickets.kindPwyw', { defaultValue: 'Pay what you want' });
    }

    if ((tk.price ?? 0) > 0) {
      return formatCurrency(tk.price as number, tk.currencyCode || currency || 'USD');
    }

    return t('events:tickets.kindFree', { defaultValue: 'Free' });
  };

  const codeLabel = (c: DiscountCode): string =>
    c.amountOff != null
      ? t('events:wizard.tickets.amountOff', {
          defaultValue: '{{amount}} off',
          amount: formatCurrency(c.amountOff, c.currencyCode || currency || 'USD'),
        })
      : t('events:wizard.tickets.percentOff', { defaultValue: '{{percent}}% off', percent: c.percentOff ?? 0 });

  if (!eventId) {
    // Unreachable by the step order (the event is created on leaving Guests) — said rather than rendered blank.
    return (
      <Text size="md" c="dimmed">
        {t('events:wizard.tickets.noEvent', { defaultValue: 'Your event is created at the end of the Guests step.' })}
      </Text>
    );
  }

  const busy = wizardSaving || sellingSaving;

  // ── 1. How you get paid ─────────────────────────────────────────────────────────────────────────────────────────
  const renderSelling = () => {
    if (eventError) {
      return (
        <Stack gap="sm">
          <ErrorAlert
            error={eventError}
            title={t('events:wizard.tickets.eventLoadFailed', { defaultValue: 'Could not load the event’s selling settings' })}
            page="Create event — tickets"
            operation="eventGet"
          />
          <Group>
            <Button variant="light" size="md" onClick={() => void reloadEvent()} leftSection={<AppIcon iconName="Refresh" size={16} />}>
              {t('common:retry', { defaultValue: 'Try again' })}
            </Button>
          </Group>
        </Stack>
      );
    }

    if (!event) {
      return <Skeleton height={120} radius="md" />;
    }

    return (
      <Stack gap="sm">
        <Grid gutter="sm">
          <Grid.Col span={{ base: 12, sm: 4 }}>
            <SelectField
              name="currencyCode"
              label={t('events:details.currency', { defaultValue: 'Currency' })}
              description={t('events:details.currencyHint', {
                defaultValue: 'Everything on this event is priced in it. Locks once money is recorded.',
              })}
              data={currencyData}
              value={event.currencyCode ?? ''}
              onChange={(_name, value) => {
                if (value && value !== event.currencyCode) {
                  void saveSelling({ currencyCode: value as string });
                }
              }}
              searchable
              required
              allowDeselect={false}
              disabled={busy}
              error={sellingFieldErrors.currencyCode}
            />
          </Grid.Col>
          <Grid.Col span={{ base: 12, sm: 4 }}>
            <SelectField
              name="feeMode"
              label={t('events:selling.feeMode', { defaultValue: 'Who pays the fees' })}
              description={t('events:selling.feeModeHint', {
                defaultValue: 'Our service fee is 2% (min 0.50, max 20.00) per order. Card fees are estimated.',
              })}
              data={feeModeData}
              value={feeModeOf(event)}
              onChange={(_name, value) => {
                if (value && value !== feeModeOf(event)) {
                  // ⚠ Both columns, always — see `utils/eventSelling`.
                  void saveSelling({ feeMode: value as string, serviceFeePayer: serviceFeePayerFor(value as string) });
                }
              }}
              allowDeselect={false}
              disabled={busy}
              error={sellingFieldErrors.feeMode}
            />
          </Grid.Col>
          <Grid.Col span={{ base: 12, sm: 4 }}>
            <TaxSelect
              name="taxSettingId"
              description={t('events:selling.taxRecordHint', { defaultValue: "The event's default rate — a ticket type can override it" })}
              value={event.taxSettingId ?? null}
              onChange={(_name, value) => {
                if ((value ?? null) !== (event.taxSettingId ?? null)) {
                  void saveSelling({ taxSettingId: value ?? null });
                }
              }}
              disabled={busy}
              error={sellingFieldErrors.taxSettingId}
            />
          </Grid.Col>
        </Grid>

        {sellingGeneric.length > 0 ? (
          <Alert variant="light" color="red" icon={<AppIcon iconName="AlertCircle" size={18} />}>
            <Text size="md">{formatErrors(sellingGeneric)}</Text>
          </Alert>
        ) : null}

        {renderCardPayments()}
      </Stack>
    );
  };

  const renderCardPayments = () => (
    <CardPaymentsNotice
      payments={cardPayments}
      paid={hasPaidTicket}
      isTestMode={!!event?.isTestMode}
      offBody={t('events:wizard.tickets.cardsOff', {
        defaultValue:
          'Free tickets and sales at the door work now. To sell paid tickets online, connect your Stripe account — the money goes straight to you.',
      })}
    />
  );

  // ── 2. Ticket types ─────────────────────────────────────────────────────────────────────────────────────────────
  const renderTickets = () => {
    // Not read yet → skeleton; the read FAILED → the error (never "no tickets"); none → suggestions; else the rows.
    if (!ticketsLoaded) {
      return (
        <Stack gap="xs">
          <Skeleton height={60} radius="md" />
          <Skeleton height={60} radius="md" />
        </Stack>
      );
    }

    if (ticketsError) {
      return (
        <Stack gap="sm">
          <ErrorAlert
            error={ticketsError}
            title={t('events:wizard.tickets.ticketsLoadFailed', { defaultValue: 'Could not load the ticket types' })}
            page="Create event — tickets"
            operation="ticketTypeList"
          />
          <Group>
            <Button variant="light" size="md" onClick={() => void reloadTickets()} leftSection={<AppIcon iconName="Refresh" size={16} />}>
              {t('common:retry', { defaultValue: 'Try again' })}
            </Button>
          </Group>
        </Stack>
      );
    }

    if (tickets.length === 0) {
      return (
        <Paper withBorder radius="md" p="md">
          <Stack gap="sm">
            <Text size="md" c="dimmed">
              {t('events:wizard.tickets.noTickets', {
                defaultValue: 'No tickets yet — guests cannot buy anything until you add one. Start with one of these, or add your own:',
              })}
            </Text>
            <Group gap="xs">
              {TICKET_SUGGESTIONS.map((s) => (
                <Button
                  key={s.key}
                  variant="light"
                  size="md"
                  leftSection={<AppIcon iconName="Plus" size={16} />}
                  disabled={!ticketGate.canCreate || busy}
                  onClick={() => openTicket(null, t(`events:wizard.tickets.suggest.${s.key}`, { defaultValue: s.label }))}
                >
                  {t(`events:wizard.tickets.suggest.${s.key}`, { defaultValue: s.label })}
                </Button>
              ))}
            </Group>
          </Stack>
        </Paper>
      );
    }

    return (
      <Stack gap="xs">
        {tickets.map((tk) => {
          const details = [
            priceLabel(tk),
            tk.quantityTotal != null
              ? t('events:wizard.tickets.available', { defaultValue: '{{count}} available', count: tk.quantityTotal })
              : t('events:tickets.unlimited', { defaultValue: 'Unlimited' }),
          ];

          return (
            <Paper key={tk.id} withBorder radius="md" p="sm">
              <Group justify="space-between" wrap="nowrap" align="flex-start">
                <Stack gap={2} style={{ minWidth: 0 }}>
                  <Group gap="xs" wrap="wrap">
                    <Text size="md" fw={600}>
                      {tk.name}
                    </Text>
                    {tk.isAddOn ? (
                      <Badge size="md" variant="light" color="gray">
                        {t('events:wizard.tickets.addOn', { defaultValue: 'Add-on' })}
                      </Badge>
                    ) : null}
                  </Group>
                  <Text size="md" c="dimmed">
                    {details.join(' · ')}
                  </Text>
                  <Group gap="xs" mt={4} wrap="wrap">
                    <Button
                      variant="light"
                      color="cyan"
                      size="compact-md"
                      leftSection={<AppIcon iconName="Discount" size={16} />}
                      disabled={busy}
                      onClick={() => setRulesFor(tk)}
                    >
                      {(tk.priceRules?.length ?? 0) > 0
                        ? t('events:tickets.rulesBadge', { defaultValue: '{{count}} rule(s)', count: tk.priceRules?.length ?? 0 })
                        : t('events:wizard.tickets.priceRules', { defaultValue: 'Price rules' })}
                    </Button>
                    {vouchersOn ? (
                      <Button
                        variant="light"
                        color="grape"
                        size="compact-md"
                        leftSection={<AppIcon iconName="RosetteDiscount" size={16} />}
                        disabled={busy}
                        onClick={() => setGrantsFor(tk)}
                      >
                        {t('events:wizard.tickets.grants', { defaultValue: 'Vouchers per ticket' })}
                      </Button>
                    ) : null}
                  </Group>
                </Stack>
                <Group gap={4} wrap="nowrap">
                  <ActionIcon
                    variant="subtle"
                    size="lg"
                    aria-label={t('common:actions.edit', { defaultValue: 'Edit' })}
                    disabled={busy}
                    onClick={() => openTicket(tk)}
                  >
                    <AppIcon iconName="Pencil" size={18} />
                  </ActionIcon>
                  <ActionIcon
                    variant="subtle"
                    color="red"
                    size="lg"
                    aria-label={t('common:actions.remove', { defaultValue: 'Remove' })}
                    disabled={busy}
                    onClick={() => setPendingRemove({ kind: 'ticket', id: tk.id as string, name: tk.name ?? '' })}
                  >
                    <AppIcon iconName="Trash" size={18} />
                  </ActionIcon>
                </Group>
              </Group>
            </Paper>
          );
        })}
        <LoadMoreButton hasMore={hasMore} loading={ticketsLoading && !ticketsLoaded} loadingMore={loadingMore} onLoadMore={() => void loadMore()} />
      </Stack>
    );
  };

  // ── 3. Discount codes ───────────────────────────────────────────────────────────────────────────────────────────
  const renderCodes = () => {
    if (!codesLoaded) {
      return <Skeleton height={48} radius="md" />;
    }

    if (codesError) {
      return (
        <Stack gap="sm">
          <ErrorAlert
            error={codesError}
            title={t('events:wizard.tickets.codesLoadFailed', { defaultValue: 'Could not load the discount codes' })}
            page="Create event — tickets"
            operation="discountCodeList"
          />
          <Group>
            <Button variant="light" size="md" onClick={() => void reloadCodes()} leftSection={<AppIcon iconName="Refresh" size={16} />}>
              {t('common:retry', { defaultValue: 'Try again' })}
            </Button>
          </Group>
        </Stack>
      );
    }

    if (codes.length === 0) {
      return (
        <Text size="md" c="dimmed">
          {t('events:wizard.tickets.noCodes', {
            defaultValue: 'None yet. A code takes a percentage or an amount off — for early birds, partners or your newsletter.',
          })}
        </Text>
      );
    }

    return (
      <Stack gap="xs">
        {codes.map((c) => (
          <Paper key={c.id} withBorder radius="md" p="sm">
            <Group justify="space-between" wrap="nowrap" align="flex-start">
              <Stack gap={2} style={{ minWidth: 0 }}>
                <Text size="md" fw={600} ff="monospace">
                  {c.code}
                </Text>
                <Text size="md" c="dimmed">
                  {[
                    codeLabel(c),
                    c.maxUses != null
                      ? t('events:wizard.tickets.maxUses', { defaultValue: 'up to {{count}} uses', count: c.maxUses })
                      : null,
                  ]
                    .filter(Boolean)
                    .join(' · ')}
                </Text>
              </Stack>
              <Group gap={4} wrap="nowrap">
                <ActionIcon
                  variant="subtle"
                  size="lg"
                  aria-label={t('common:actions.edit', { defaultValue: 'Edit' })}
                  disabled={busy}
                  onClick={() => openCode(c)}
                >
                  <AppIcon iconName="Pencil" size={18} />
                </ActionIcon>
                <ActionIcon
                  variant="subtle"
                  color="red"
                  size="lg"
                  aria-label={t('common:actions.remove', { defaultValue: 'Remove' })}
                  disabled={busy}
                  onClick={() => setPendingRemove({ kind: 'code', id: c.id as string, name: c.code ?? '' })}
                >
                  <AppIcon iconName="Trash" size={18} />
                </ActionIcon>
              </Group>
            </Group>
          </Paper>
        ))}
      </Stack>
    );
  };

  const sectionTitle = (label: string, action?: React.ReactNode) => (
    <Group justify="space-between" wrap="nowrap" align="center">
      <Text fw={600} size="md">
        {label}
      </Text>
      {action}
    </Group>
  );

  return (
    <Stack gap="xl">
      <Text size="md" c="dimmed">
        {t('events:wizard.tickets.intro', {
          defaultValue: 'Set up what you sell. Everything on this step is saved to your event as you go.',
        })}
      </Text>

      <Stack gap="sm">
        {sectionTitle(t('events:wizard.tickets.sellingTitle', { defaultValue: 'How you get paid' }))}
        {renderSelling()}
      </Stack>

      <Stack gap="sm">
        {sectionTitle(
          t('events:wizard.tickets.ticketsTitle', { defaultValue: 'Ticket types' }),
          tickets.length > 0 ? (
            <Button
              variant="light"
              size="md"
              leftSection={<AppIcon iconName="Plus" size={16} />}
              disabled={!ticketGate.canCreate || busy}
              onClick={() => openTicket(null)}
            >
              {t('events:wizard.tickets.addTicket', { defaultValue: 'Add a ticket' })}
            </Button>
          ) : undefined,
        )}
        <PlanFeatureNotice
          verdict={ticketGate.verdict}
          onUpgrade={ticketGate.goToUpgrade}
          what={t('events:tickets.anotherTicket', { defaultValue: 'another ticket type' })}
          gate="add"
        />
        {renderTickets()}
      </Stack>

      <Stack gap="sm">
        {sectionTitle(
          t('events:wizard.tickets.codesTitle', { defaultValue: 'Discount codes (optional)' }),
          <Button
            variant="light"
            size="md"
            leftSection={<AppIcon iconName="Plus" size={16} />}
            disabled={busy}
            onClick={() => openCode(null)}
          >
            {t('events:wizard.tickets.addCode', { defaultValue: 'Add a code' })}
          </Button>,
        )}
        {renderCodes()}
      </Stack>

      <Paper withBorder radius="md" p="md">
        <Group gap="sm" align="flex-start" wrap="nowrap">
          <Box pt={4}>
            <AppIcon iconName="Gift" size={18} />
          </Box>
          <Stack gap={4}>
            <Text fw={600} size="md">
              {t('events:wizard.tickets.vouchersTitle', { defaultValue: 'Vouchers' })}
            </Text>
            <Text size="md" c="dimmed">
              {vouchersOn
                ? t('events:wizard.tickets.vouchersOn', {
                    defaultValue: 'Choose how many vouchers each ticket gives with “Vouchers per ticket” on its row.',
                  })
                : t('events:wizard.tickets.vouchersOff', {
                    defaultValue:
                      'Give buyers something extra with their ticket — a free drink, a merch pickup, a parking pass. Go back to Features and switch Vouchers on: you create them first, then choose how many each ticket gives.',
                  })}
            </Text>
            {voucherIdea && !vouchersOn ? (
              <Text size="md" c="dimmed">
                {t('events:features.idea', { defaultValue: 'Idea: {{idea}}', idea: voucherIdea })}
              </Text>
            ) : null}
          </Stack>
        </Group>
      </Paper>

      <TicketDrawer
        opened={!!ticketDrawer}
        onClose={() => setTicketDrawer(null)}
        eventId={eventId}
        eventCurrency={currency}
        ticket={ticketDrawer?.ticket ?? null}
        defaultName={ticketDrawer?.defaultName}
        onSaved={() => void reloadTickets()}
        recurring={recurring}
        timezone={draft.timezone || null}
      />

      <TicketRulesDrawer
        opened={!!rulesFor}
        onClose={() => setRulesFor(null)}
        ticket={rulesFor}
        eventCurrency={currency}
        timezone={draft.timezone || null}
        onChanged={() => void reloadTickets()}
        locked={promotionsLocked}
      />

      <TicketCouponGrantsDrawer
        opened={!!grantsFor}
        onClose={() => setGrantsFor(null)}
        ticket={grantsFor}
        onChanged={() => void reloadTickets()}
        locked={promotionsLocked}
      />

      <DiscountCodeDrawer
        opened={!!codeDrawer}
        onClose={() => setCodeDrawer(null)}
        eventId={eventId}
        eventCurrency={currency}
        code={codeDrawer?.code ?? null}
        onSaved={() => void reloadCodes()}
        timezone={draft.timezone || null}
      />

      <ConfirmModal
        opened={!!pendingRemove}
        onClose={() => setPendingRemove(null)}
        onConfirm={() => void confirmRemove()}
        type="danger"
        confirmInProgress={removing}
        title={
          pendingRemove?.kind === 'code'
            ? t('events:wizard.tickets.removeCodeTitle', { defaultValue: 'Remove this discount code?' })
            : t('events:wizard.tickets.removeTicketTitle', { defaultValue: 'Remove this ticket type?' })
        }
        message={t('events:wizard.tickets.removeMessage', { defaultValue: '“{{name}}” will be removed from this event.', name: pendingRemove?.name ?? '' })}
        confirmLabel={t('common:actions.remove', { defaultValue: 'Remove' })}
      />
    </Stack>
  );
};
